import { Box, Grid } from "@mui/material";
import { endOfDay, isAfter, startOfMonth, subMonths } from "date-fns";
import { useEffect } from "react";
import { Field } from "react-final-form";
import t from "../../../../../_constantes/erreursConstantes.json";
import Libelle from "../../../../../_i18n/fr.json";
import Divider from "../../../../../components/Divider";
import FormButtons from "../../../../../components/FormButtons";
import FormContainer from "../../../../../components/FormContainer";
import InputField from "../../../../../components/input/InputField";
import MessageInfo from "../../../../../components/MessageInfo";
import { formatApiDate } from "../../../../../helpers/formatters";
import { getTitleCandidat } from "../../../../../helpers/utils";
import {
  compareDates,
  estApeValide,
  estSiretValide,
  isActiviteAvant1Month,
  isDateInfAujourdhui,
  isInteger,
} from "../../../../../helpers/validators";
import revenuFiscal from "../../../../../ressources/images/rev-fisc-ref.png";
import revenuIndividuelDeclare from "../../../../../ressources/images/rev-ind-dec.png";
import {
  DossierLocation,
  DossierLocationCompositionLocationEnum,
  DossierParticulierDetailTypeCompteEnum,
  SituationProfessionnelle,
} from "../../../../../services/Api/swagger";
import {
  getConditionsAffichage,
  IConditionSituationPro,
  ISituationProfessionnellePresentation,
  isRevenuDeclareOk,
  isRevenuFiscalOk,
  showRevenusAnnuels,
  SituationProfessionnelleFormData,
  SituationProfessionnelleFormError,
} from "../../helpers";
import styles from "../../style";
import AjouterSituationProfessionnelleUnePersonne from "./AjouterSituationProfessionnelleUnePersonne";

interface Props {
  formValues?: SituationProfessionnelleFormData;
  initValues?: SituationProfessionnelleFormData;
  listSituationProfessionnelle?: SituationProfessionnelle[];
  dossierLocation?: DossierLocation;
  handleChangeStatutProfessionnelle?: any;
  change: <F extends keyof SituationProfessionnelleFormData>(
    name: F,
    value?: SituationProfessionnelleFormData[F] | undefined
  ) => void;
  situationProFetching: boolean;
  onPrecedent?: any;
  typeCompte?: DossierParticulierDetailTypeCompteEnum;
  refDossierLocataire?: string;
  selectedDossier?: string;
  selectedPerson?: string;
  nomPrenomLocataire?: string;
  onSubmit: any;
  compositionLocation?: DossierLocationCompositionLocationEnum;
  isCumulsUtilises: boolean;
}

function AjouterSituationProfessionnelleForm({
  listSituationProfessionnelle,
  onSubmit,
  formValues,
  dossierLocation,
  onPrecedent,
  situationProFetching,
  typeCompte,
  refDossierLocataire,
  selectedDossier,
  selectedPerson,
  nomPrenomLocataire,
  change,
  compositionLocation,
  isCumulsUtilises,
}: Props) {
  useEffect(() => {
    if (
      formValues?.principal &&
      !showRevenusAnnuels(formValues.principal) &&
      formValues.principal.revenuIndividuelDeclare &&
      formValues.principal.codeStatutProfessionnel
    ) {
      // @ts-ignore
      change("principal.revenuIndividuelDeclare", undefined);
      // @ts-ignore
      change("principal.revenuFiscalReference", undefined);
    }
  }, [formValues?.principal]);

  useEffect(() => {
    if (
      formValues?.secondaire &&
      !showRevenusAnnuels(formValues.secondaire) &&
      formValues.secondaire.revenuIndividuelDeclare &&
      formValues.secondaire.codeStatutProfessionnel
    ) {
      // @ts-ignore
      change("secondaire.revenuIndividuelDeclare", undefined);
      // @ts-ignore
      change("secondaire.revenuFiscalReference", undefined);
    }
  }, [formValues?.secondaire]);

  // calcul revenus fiscal de reference si célibataire
  const messageRevenusFiscal =
    formValues?.principal?.revenuFiscalReference &&
    compositionLocation &&
    isRevenuFiscalOk(
      formValues,
      formValues.principal?.revenuFiscalReference,
      compositionLocation
    );

  const messageRevenusDeclarePrincipal = isRevenuDeclareOk(
    formValues?.principal?.revenuIndividuelDeclare?.toString(),
    formValues?.principal?.salaireNetImposableM1?.toString(),
    formValues?.principal?.salaireNetImposableM2?.toString(),
    formValues?.principal?.salaireNetImposableM3?.toString(),
    " du candidat."
  );

  const messageRevenusDeclareSecondaire = isRevenuDeclareOk(
    formValues?.secondaire?.revenuIndividuelDeclare?.toString(),
    formValues?.secondaire?.salaireNetImposableM1?.toString(),
    formValues?.secondaire?.salaireNetImposableM2?.toString(),
    formValues?.secondaire?.salaireNetImposableM3?.toString(),
    " du conjoint."
  );

  const isCouple = compositionLocation === "COUPLE";
  const showRevenusCandidat =
    formValues?.principal && showRevenusAnnuels(formValues?.principal);

  const showRevenusConjoint =
    formValues?.secondaire && showRevenusAnnuels(formValues.secondaire);

  const labelRevenu = isCouple
    ? "Revenu individuel déclaré candidat"
    : "Revenu individuel déclaré";

  const findTypeCompte = formValues?.principal?.typeCompte || typeCompte;
  const titlePage =
    dossierLocation &&
    formValues &&
    findTypeCompte &&
    getTitleCandidat(
      dossierLocation,
      findTypeCompte,
      dossierLocation.dossiersParticuliers,
      refDossierLocataire || undefined,
      selectedPerson || undefined,
      nomPrenomLocataire || undefined
    );

  const textSection1 =
    formValues?.principal?.typeCompte === "GARANT" || typeCompte === "GARANT"
      ? "sectionGarant"
      : "section1";

  const isInvalid =
    formValues?.principal?.codeStatutProfessionnel === "DIPLOM" ||
    formValues?.principal?.codeStatutProfessionnel === "AMBASS" ||
    formValues?.principal?.codeStatutProfessionnel === "RENT";

  const isInvalidSecondaire =
    formValues?.secondaire?.codeStatutProfessionnel === "DIPLOM" ||
    formValues?.secondaire?.codeStatutProfessionnel === "AMBASS" ||
    formValues?.secondaire?.codeStatutProfessionnel === "RENT";

  const isDossierCouple =
    compositionLocation === DossierLocationCompositionLocationEnum["COUPLE"];

  return (
    <>
      <h5 className="titlePage">{titlePage}</h5>
      <Box component="form" sx={styles} onSubmit={onSubmit} noValidate>
        <h3>{Libelle.pageSituationPerso.sections[textSection1]}</h3>
        <AjouterSituationProfessionnelleUnePersonne
          prefixe="principal"
          situationsPros={listSituationProfessionnelle}
          formValues={formValues && formValues.principal}
          change={change}
          isCumulsUtilises={isCumulsUtilises}
        />
        {isCouple && findTypeCompte !== "GARANT" && (
          <>
            <Divider />
            <h3>{Libelle.pageSituationPro.sections.section2}</h3>
            <AjouterSituationProfessionnelleUnePersonne
              prefixe="secondaire"
              situationsPros={listSituationProfessionnelle}
              formValues={formValues && formValues.secondaire}
              change={change}
              isCumulsUtilises={isCumulsUtilises}
            />
          </>
        )}

        {(showRevenusCandidat || showRevenusConjoint) && (
          <>
            <h3>{Libelle.pageSituationPro.sections.section3}</h3>
            <FormContainer>
              <Grid container spacing={2}>
                {showRevenusCandidat && (
                  <>
                    <Grid item xs={3}>
                      <Field
                        name="principal.revenuIndividuelDeclare"
                        component={InputField}
                        type="number"
                        max={9999999}
                        label={labelRevenu}
                        required
                        imageHelpInfo={revenuIndividuelDeclare}
                        numberAfterDecimal={0}
                      />
                    </Grid>
                  </>
                )}
                {showRevenusConjoint && (
                  <Grid item xs={3}>
                    <Field
                      name="secondaire.revenuIndividuelDeclare"
                      component={InputField}
                      type="number"
                      max={9999999}
                      label="Revenu individuel déclaré conjoint"
                      required
                      imageHelpInfo={revenuIndividuelDeclare}
                      numberAfterDecimal={0}
                    />
                  </Grid>
                )}
                {formValues &&
                  ((showRevenusConjoint &&
                    !showRevenusCandidat &&
                    formValues.secondaire?.revenuFiscalReference) ||
                    formValues.principal?.revenuFiscalReference) && (
                    <Grid item xs={3}>
                      <Field
                        name={
                          showRevenusConjoint && !showRevenusCandidat
                            ? "secondaire.revenuFiscalReference"
                            : "principal.revenuFiscalReference"
                        }
                        component={InputField}
                        type="number"
                        max={9999999}
                        label="Revenu fiscal de référence imposable"
                        imageHelpInfo={revenuFiscal}
                        required={isDossierCouple}
                        note={
                          !isDossierCouple &&
                          t.formulaire.generale
                            .referenceFiscalAvertissementCasAutreQueCouple
                        }
                        classNote={!isDossierCouple && "classNote"}
                        numberAfterDecimal={0}
                      />
                    </Grid>
                  )}
              </Grid>
              {messageRevenusFiscal && (
                <MessageInfo messageUrl={messageRevenusFiscal} />
              )}
              {messageRevenusDeclarePrincipal && (
                <MessageInfo messageUrl={messageRevenusDeclarePrincipal} />
              )}
              {messageRevenusDeclareSecondaire && (
                <MessageInfo messageUrl={messageRevenusDeclareSecondaire} />
              )}
            </FormContainer>
          </>
        )}
        <FormButtons
          onPrecedent={onPrecedent}
          fetching={situationProFetching}
          canCancel={selectedDossier}
          disabledButton={isInvalid || isInvalidSecondaire}
          messageButton={
            "Votre dossier ne peut être traité dans l'immédiat. Merci de bien vouloir contacter votre plateforme Vertuloo"
          }
        />
      </Box>
    </>
  );
}

export const validerSituationProfessionnelle = (
  form: ISituationProfessionnellePresentation,
  cv: IConditionSituationPro,
  compositionLocation?: DossierLocationCompositionLocationEnum,
  isCumulsUtilises?: boolean
) => {
  const errors = {} as any;

  // SITUATION
  if (!form?.situation) {
    errors.situation = t.formulaire.generale.champObligatoire;
  }

  // STATUT
  if (
    (cv.isSalarie || cv.isNonSalarieOrAutre) &&
    !form?.codeStatutProfessionnel
  ) {
    errors.codeStatutProfessionnel = t.formulaire.generale.champObligatoire;
  }

  // DATE DE SITUATION
  if (!form?.dateDebutSituationProfessionnelle) {
    errors.dateDebutSituationProfessionnelle =
      t.formulaire.generale.champObligatoire;
  }

  if (form && cv.isCdiEssaiOrCddOrInterimaire) {
    if (!form.dateFinSituationProfessionnelle) {
      errors.dateFinSituationProfessionnelle =
        t.formulaire.generale.champObligatoire;
    } else if (
      form.dateDebutSituationProfessionnelle &&
      !compareDates(
        form.dateFinSituationProfessionnelle,
        form.dateDebutSituationProfessionnelle,
        true
      )
    ) {
      errors.dateFinSituationProfessionnelle =
        t.formulaire.generale.dateSituationIncorrecte;
    }
  }

  if (form && cv.isCdiEssai && cv.isDateDebutActivitePlus1an) {
    errors.dateDebutSituationProfessionnelle =
      t.formulaire.generale.dateSituationCDIEssai;
  }

  if (
    form &&
    form.dateDebutSituationProfessionnelle &&
    form.codeStatutProfessionnel === "ETU" &&
    isAfter(
      new Date(form.dateDebutSituationProfessionnelle),
      endOfDay(new Date(new Date().getFullYear(), 11, 31))
    )
  ) {
    errors.dateDebutSituationProfessionnelle =
      "La date de début de situation actuelle dépasse le 31/12 de l'année en cours";
  }
  // NOMBRE MOIS TRAVAILLES
  if (form && (cv.isSalarie || cv.isNonSalarie || cv.isRechercheEmploi)) {
    if (form.nombreMoisTravailles === undefined) {
      errors.nombreMoisTravailles = t.formulaire.generale.champObligatoire;
    } else if (!isInteger(form.nombreMoisTravailles)) {
      errors.nombreMoisTravailles = t.formulaire.generale.champEntierErreur;
    } else if (Number(form.nombreMoisTravailles) > 24) {
      errors.nombreMoisTravailles =
        t.formulaire.generale.nombreMoisTravaillesSup24Mois;
    }
  }

  // DATE DERNIER BULLETIN
  const minus2Month = subMonths(startOfMonth(new Date()), 2);
  const dateDernierBulletinDateMin = formatApiDate(minus2Month);
  if (
    form &&
    cv.isSalarie &&
    isActiviteAvant1Month(form.dateDebutSituationProfessionnelle)
  ) {
    if (!form.dateDernierBulletin) {
      errors.dateDernierBulletin = t.formulaire.generale.champObligatoire;
    } else if (
      !isDateInfAujourdhui(form.dateDernierBulletin) ||
      !compareDates(form.dateDernierBulletin, dateDernierBulletinDateMin, false)
    ) {
      errors.dateDernierBulletin = "Merci de saisir M-1 ou M-2";
    }
  }

  // SALAIRES
  if (form && cv.isSalarie && cv.isDateActiviteAvant45j) {
    if (!form.salaireNetImposableM1) {
      errors.salaireNetImposableM1 = t.formulaire.generale.champObligatoire;
    } else if (!isInteger(form.salaireNetImposableM1)) {
      errors.salaireNetImposableM1 = t.formulaire.generale.champEntierErreur;
    }
  }

  if (form && cv.isSalarie && cv.isDateActiviteAvant75j) {
    if (!form.salaireNetImposableM2) {
      errors.salaireNetImposableM2 = t.formulaire.generale.champObligatoire;
    } else if (!isInteger(form.salaireNetImposableM2)) {
      errors.salaireNetImposableM2 = t.formulaire.generale.champEntierErreur;
    }
  }

  if (form && cv.isSalarie && cv.isDateActiviteAvant105j) {
    if (!form.salaireNetImposableM3) {
      errors.salaireNetImposableM3 = t.formulaire.generale.champObligatoire;
    } else if (!isInteger(form.salaireNetImposableM3)) {
      errors.salaireNetImposableM3 = t.formulaire.generale.champEntierErreur;
    }
  }

  // SALAIRE BRUT/NET MENSUEL
  if (
    form &&
    cv.isSalarie &&
    !isActiviteAvant1Month(form.dateDebutSituationProfessionnelle)
  ) {
    if (!form.salaireBrutMensuel) {
      errors.salaireBrutMensuel = t.formulaire.generale.champObligatoire;
    }
  }

  // CUMUL  SALAIRES
  const salarieDateDebutPlusEqual1an =
    cv.isSalarie && cv.isDateDebutActivitePlusEqual1an && isCumulsUtilises;
  if (salarieDateDebutPlusEqual1an && cv.isDateActiviteAvant45j) {
    if (!form.cumulSalaireNetImposableM1) {
      errors.cumulSalaireNetImposableM1 =
        t.formulaire.generale.champObligatoire;
    } else if (!isInteger(form.cumulSalaireNetImposableM1)) {
      errors.cumulSalaireNetImposableM1 =
        t.formulaire.generale.champEntierErreur;
    }
  }

  if (salarieDateDebutPlusEqual1an && cv.isDateActiviteAvant75j) {
    if (!form.cumulSalaireNetImposableM2) {
      errors.cumulSalaireNetImposableM2 =
        t.formulaire.generale.champObligatoire;
    } else if (!isInteger(form.cumulSalaireNetImposableM2)) {
      errors.cumulSalaireNetImposableM2 =
        t.formulaire.generale.champEntierErreur;
    }
  }

  if (salarieDateDebutPlusEqual1an && cv.isDateActiviteAvant105j) {
    if (!form.cumulSalaireNetImposableM3) {
      errors.cumulSalaireNetImposableM3 =
        t.formulaire.generale.champObligatoire;
    } else if (!isInteger(form.cumulSalaireNetImposableM3)) {
      errors.cumulSalaireNetImposableM3 =
        t.formulaire.generale.champEntierErreur;
    }
  }

  // CODE APE
  if (form && (form.situation === "SPR" || form.situation === "TNS")) {
    if (!form.codeApe) {
      errors.codeApe = t.formulaire.generale.champObligatoire;
    } else if (!estApeValide(form.codeApe)) {
      errors.codeApe = t.formulaire.generale.formatCodeApeInvalide;
    }
  }

  // SIRET
  if (form && cv.isNonSalarie) {
    if (!form.siret) {
      errors.siret = t.formulaire.generale.champObligatoire;
    } else if (!estSiretValide(form.siret)) {
      errors.siret = t.formulaire.generale.formatChampInvalide;
    }
  }

  // REVENU FISCAL REF
  if (form && form.revenuFiscalReference && showRevenusAnnuels(form)) {
    if (
      !form.revenuFiscalReference &&
      compositionLocation === DossierLocationCompositionLocationEnum["COUPLE"]
    ) {
      errors.revenuFiscalReference = t.formulaire.generale.champObligatoire;
    } else if (
      form.revenuFiscalReference &&
      !isInteger(form.revenuFiscalReference)
    ) {
      errors.revenuFiscalReference = t.formulaire.generale.champEntierErreur;
    }
  }

  if (form && showRevenusAnnuels(form)) {
    if (!form.revenuIndividuelDeclare) {
      errors.revenuIndividuelDeclare = t.formulaire.generale.champObligatoire;
    } else if (!isInteger(form.revenuIndividuelDeclare)) {
      errors.revenuIndividuelDeclare = t.formulaire.generale.champEntierErreur;
    }
  }
  return errors;
};

export const validateForm = (
  values: SituationProfessionnelleFormData,
  compositionLocation?: DossierLocationCompositionLocationEnum,
  isCumulsUtilises?: boolean
): SituationProfessionnelleFormError => {
  const errors: SituationProfessionnelleFormError =
    {} as SituationProfessionnelleFormError;
  const conditionsAffichagePrincipal = getConditionsAffichage(
    values && values.principal
  );
  const conditionsAffichageSecondaire = getConditionsAffichage(
    values && values.secondaire
  );
  errors.principal = validerSituationProfessionnelle(
    values.principal,
    conditionsAffichagePrincipal,
    compositionLocation,
    isCumulsUtilises
  );
  errors.secondaire =
    values.secondaire &&
    validerSituationProfessionnelle(
      values.secondaire,
      conditionsAffichageSecondaire,
      compositionLocation,
      isCumulsUtilises
    );
  return errors;
};

export default AjouterSituationProfessionnelleForm;
